import { DateTime } from 'luxon';
import { useCallback } from 'react';
import {
    NameType,
    ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { TooltipProps } from 'recharts/types/component/Tooltip';

import styles from '../../styles/metrics/MetricChartTooltip.module.scss';
import { ChartDataType } from './MetricChart';

const formatter = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
});

type MetricChartTooltipProps = TooltipProps<ValueType, NameType> & {
    chartDataType: ChartDataType;
};

function MetricChartTooltip({
    active,
    payload,
    label,
    chartDataType,
}: MetricChartTooltipProps) {
    const datetime = DateTime.fromISO(label);

    const formatValue = useCallback(
        (value: ValueType | undefined) => {
            if (typeof value === 'string') return value;
            if (value == null || typeof value !== 'number') return null;

            // handle special formatting cases
            if (chartDataType === 'tripsPerDay') {
                return value === 0 ? '<50' : formatter.format(value);
            }
            if (chartDataType === 'tripsPerVehiclePerDay') {
                return value.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                });
            }
            // otherwise return our default formatter
            return formatter.format(value);
        },
        [chartDataType]
    );

    if (!active || !payload || !payload.length) {
        return null;
    }

    return (
        <div className={styles.tooltip}>
            <span className={styles.title}>
                {datetime.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
            </span>
            {Object.values(payload).map(item => (
                <div key={item.name} className={styles.keyValue}>
                    <span className={styles.key}>{item.name}:</span>
                    <span className={styles.value}>
                        {formatValue(item.value)}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default MetricChartTooltip;
